import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
  subYears,
} from 'date-fns';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  IonItem,
  IonList,
  IonSpinner,
  IonIcon,
  IonSelect,
  IonSelectOption,
  IonLabel,
  IonItemDivider
} from '@ionic/react';
import { downloadOutline } from 'ionicons/icons';

import { getReport } from '@/actions/companies';
import { requireRole } from '@/helpers/permissions';
import { saveTextFile } from '@/helpers/platform';
import Page from '@/pages/Page';
import ValidatedInput from '@/components/ValidatedInput';

const today = new Date();
const DATE_FORMAT = 'yyyy-MM-dd';
const DATE_RANGES = [
  {
    label: 'Last Month',
    start: format(startOfMonth(subMonths(today, 1)), DATE_FORMAT),
    end: format(endOfMonth(subMonths(today, 1)), DATE_FORMAT),
  },
  {
    label: 'This Month',
    start: format(startOfMonth(today), DATE_FORMAT),
    end: format(today, DATE_FORMAT),
  },
  {
    label: 'Last Year',
    start: format(startOfYear(subYears(today, 1)), DATE_FORMAT),
    end: format(endOfYear(subYears(today, 1)), DATE_FORMAT),
  },
  {
    label: 'This Year to Date',
    start: format(startOfYear(today), DATE_FORMAT),
    end: format(today, DATE_FORMAT),
  },
  {
    label: 'All',
    start: '',
    end: '',
  },
];

export default requireRole(['owner', 'manager'], ReportsPage);

function ReportsPage() {
  const dispatch = useDispatch();

  const [downloading, setDownloading] = useState();
  const [errorMessage, setErrorMessage] = useState(null);

  const [dateRange, setDateRange] = useState(_.first(DATE_RANGES).label);
  const [startDate, setStartDate] = useState(_.first(DATE_RANGES).start);
  const [endDate, setEndDate] = useState(_.first(DATE_RANGES).end);

  const dateValid = !startDate || !endDate || startDate < endDate;

  const setDateFromRange = (range) => {
    setDateRange(range);
    try {
      const {start, end} = _.find(DATE_RANGES, {label: range});
      setStartDate(start);
      setEndDate(end);
    } catch (e) {
      return;
    }
  };

  const setStart = (start) => {
    setDateRange("Custom");
    setStartDate(start);
  };

  const setEnd = (end) => {
    setDateRange("Custom");
    setEndDate(end);
  };

  const downloadReport = (reportType) => async () => {
    if (downloading) return;

    setErrorMessage(null);
    setDownloading(reportType);
    try {
      const {csv, url} = await dispatch(getReport({type: reportType, startDate, endDate}));
      if (csv) {
        const dateRangeLabel = (startDate || endDate) ? `${startDate} – ${endDate}` : `as of ${format(today, DATE_FORMAT)}`;
        const filename = `${reportType} - ${dateRangeLabel}.csv`;
        await saveTextFile(csv, filename, 'text/csv;charset=utf-8');
      } else if (url) {
        window.location.href = url;
      }
    } catch (e) {
      setErrorMessage(_.get(e, 'response.data.error', e.message));
    }
    setDownloading(null);
  };

  return (
    <Page title="Reports">
      {errorMessage && <IonItem color="danger">{errorMessage}</IonItem>}
      <IonList>
        <IonItem lines="none">
          <IonSelect label="Date Range"
            labelPlacement="floating"
            name="range"
            value={dateRange}
            setValue={setDateFromRange}
          >
            {DATE_RANGES.map(({label}) => (
              <IonSelectOption key={label} value={label}>
                {label}
              </IonSelectOption>
            ))}
            <IonSelectOption value="Custom">Custom</IonSelectOption>
          </IonSelect>
        </IonItem>
        <IonItem lines="none">
          <ValidatedInput label="Start Date"
            type="date"
            name="start_date"
            value={startDate}
            setValue={setStart}
            valid={dateValid}
            errorText="Start date must be before end date" />
          <div className="ion-padding"/>
          <ValidatedInput label="End Date"
            type="date"
            name="end_date"
            value={endDate}
            setValue={setEnd}
            valid={dateValid}
            errorText="End date must be after start date"/>
        </IonItem>
        <IonItemDivider className="ion-margin-top">
          <IonLabel>Download</IonLabel>
        </IonItemDivider>
        <IonItem onClick={downloadReport("loads")} disabled={downloading} button detail={false}>
          <IonLabel>
            Loads
            <p>Includes loads started in the selected date range.</p>
          </IonLabel>
          <DownloadIcon type="loads" downloading={downloading} slot="end"/>
        </IonItem>
        <IonItem onClick={downloadReport("consumers")} disabled={downloading} button detail={false}>
          <IonLabel>
            Customers
            <p>Includes customers who have started a load in the selected date range.</p>
          </IonLabel>
          <DownloadIcon type="customers" downloading={downloading} slot="end"/>
        </IonItem>
      </IonList>
    </Page>
  );
}

function DownloadIcon({type, downloading}) {
  return downloading === type ? (
    <IonSpinner name="dots"/>
  ) : (
    <IonIcon icon={downloadOutline}/>
  )
}
